import { filter, forEach, groupBy, map, meanBy, sumBy } from "lodash";
import { getOrderPrice } from "./order";

export const QUADRANTS = {
  ESTRELLA: {
    key: "ESTRELLA",
    label: "Estrella",
    description: "Alto volumen, alto margen",
    color: "#21ba45",
  },
  CABALLO: {
    key: "CABALLO",
    label: "Caballo de batalla",
    description: "Alto volumen, bajo margen",
    color: "#f2711c",
  },
  PUZZLE: {
    key: "PUZZLE",
    label: "Dilema",
    description: "Bajo volumen, alto margen",
    color: "#2185d0",
  },
  PERRO: {
    key: "PERRO",
    label: "Lastre",
    description: "Bajo volumen, bajo margen",
    color: "#db2828",
  },
};

// Costo de los adicionales de UN pedido. Usa el costo que quedó grabado en
// el pedido al momento de la venta (Order.extras[].estimated_cost), no el
// costo actual del ExtraItem — igual que el precio, es un snapshot. Los
// adicionales agregados antes de que existiera este campo no tienen costo
// grabado y no aportan nada acá (no se excluye el producto entero por eso,
// a diferencia de cuando falta el costo del plato base).
function getOrderExtrasCost(order) {
  return sumBy(
    order.extras,
    (extra) => Number(extra.estimated_cost || 0) * extra.quantity
  );
}

function getQuadrant(quantity, margin, avgQuantity, avgMargin) {
  if (quantity >= avgQuantity && margin >= avgMargin) return QUADRANTS.ESTRELLA;
  if (quantity >= avgQuantity && margin < avgMargin) return QUADRANTS.CABALLO;
  if (quantity < avgQuantity && margin >= avgMargin) return QUADRANTS.PUZZLE;
  return QUADRANTS.PERRO;
}

// Agrupa los `Order` del rango en ventas por producto y por categoría,
// y clasifica cada producto en un cuadrante relativo al promedio de SU categoría
// (no del menú completo, para no comparar un ceviche contra una gaseosa).
export function buildProductSalesReport(orders) {
  // Los pedidos de una mesa liberada como pérdida (nunca se cobraron) no
  // cuentan como venta real — si no, un plato servido y no pagado infla
  // ingresos y margen con plata que nunca entró.
  const validOrders = filter(
    orders,
    (order) =>
      order.product_data &&
      order.payment_type !== "LOSS" &&
      order.payment_status !== "VOID"
  );
  const ordersByProduct = groupBy(validOrders, "product");

  const products = map(ordersByProduct, (productOrders) => {
    const productData = productOrders[0].product_data;
    const quantity = productOrders.length;
    const revenue = sumBy(productOrders, getOrderPrice);
    const hasCost = productData.estimated_cost != null;
    const extrasCost = sumBy(productOrders, getOrderExtrasCost);
    const cost = hasCost
      ? quantity * Number(productData.estimated_cost) + extrasCost
      : null;
    const margin = hasCost ? revenue - cost : null;

    return {
      id: productData.id,
      title: productData.title,
      categoryId: productData.category_data?.id ?? null,
      categoryTitle: productData.category_data?.title ?? "Sin categoría",
      quantity,
      revenue,
      cost,
      margin,
      hasCost,
    };
  });

  const productsByCategory = groupBy(products, "categoryTitle");

  const categories = map(productsByCategory, (categoryProducts, categoryTitle) => {
    const withCost = filter(categoryProducts, "hasCost");
    const avgQuantity = withCost.length ? meanBy(withCost, "quantity") : 0;
    const avgMargin = withCost.length ? meanBy(withCost, "margin") : 0;

    forEach(categoryProducts, (product) => {
      product.quadrant = product.hasCost
        ? getQuadrant(product.quantity, product.margin, avgQuantity, avgMargin)
        : null;
    });

    return {
      title: categoryTitle,
      quantity: sumBy(categoryProducts, "quantity"),
      revenue: sumBy(categoryProducts, "revenue"),
      avgQuantity,
      avgMargin,
      products: categoryProducts,
    };
  });

  return {
    categories,
    totalQuantity: sumBy(products, "quantity"),
    totalRevenue: sumBy(products, "revenue"),
  };
}
